<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div>
          <h2 class="text-white font-weight-bold mb-1">
            <i class="mdi mdi-storefront-outline text-warning mr-2"></i>{{ $t('redeem.title') }}
          </h2>
          <p class="text-muted small mb-0">{{ $t('redeem.subtitle') }}</p>
        </div>
        <div class="mt-2 mt-md-0">
          <router-link :to="{ name: 'CreateRedeem' }" class="btn btn-primary px-3 py-2 font-weight-bold">
            <i class="mdi mdi-plus mr-1"></i> {{ $t('redeem.addItem') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Search Bar -->
    <div class="col-12 mb-3">
      <div class="search-box">
        <div class="input-group">
          <div class="input-group-prepend">
            <span class="input-group-text bg-transparent border-0 text-muted">
              <i class="mdi mdi-magnify"></i>
            </span>
          </div>
          <input
            type="text"
            class="form-control bg-transparent border-0 text-white pl-0"
            v-model.trim="search"
            :placeholder="$t('redeem.searchPlaceholder')"
          />
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="col-12 grid-margin">
      <div class="card border border-secondary">
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0" style="table-layout: fixed; width: 100%;">
              <thead class="bg-dark text-muted small text-uppercase">
                <tr>
                  <th style="width: 45px;">No</th>
                  <th>{{ $t('redeem.itemName') }}</th>
                  <th style="width: 160px;">{{ $t('redeem.costGold') }}</th>
                  <th style="width: 120px;">{{ $t('common.status') }}</th>
                  <th class="text-right" style="width: 110px; padding-right: 1rem;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in paginatedRedeem" :key="item.id">
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="avatar-circle mr-3" style="width: 36px; height: 36px; background: rgba(245, 158, 11, 0.15); color: #f59e0b; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;">
                        <i class="mdi mdi-gift-outline"></i>
                      </div>
                      <div class="overflow-hidden">
                        <router-link :to="{ name: 'DetailRedeem', params: { id: item.id } }" class="text-white font-weight-bold text-decoration-none d-block text-truncate" :title="item.name">
                          {{ item.name }}
                        </router-link>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="badge badge-warning px-3 py-2 font-weight-bold" style="background: rgba(245, 158, 11, 0.2); color: #f59e0b; border: 1px solid rgba(245, 158, 11, 0.3);">
                      <i class="mdi mdi-circle-multiple mr-1"></i> {{ item.gold }} Gold
                    </span>
                  </td>
                  <td>
                    <span class="badge badge-success px-2 py-1 font-weight-medium">
                      {{ $t('common.active') }}
                    </span>
                  </td>
                  <td class="text-right">
                    <div class="d-inline-flex align-items-center gap-1">
                      <router-link :to="{ name: 'DetailRedeem', params: { id: item.id } }" class="btn btn-action-icon btn-outline-info" :title="$t('common.view')">
                        <i class="mdi mdi-eye"></i>
                      </router-link>
                      <router-link :to="{ name: 'EditRedeem', params: { id: item.id } }" class="btn btn-action-icon btn-outline-warning" :title="$t('common.edit')">
                        <i class="mdi mdi-pencil"></i>
                      </router-link>
                      <button type="button" class="btn btn-action-icon btn-outline-danger" @click="handleDelete(item.id)" :title="$t('common.delete')">
                        <i class="mdi mdi-delete"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="filteredItems.length === 0">
                  <td colspan="5" class="py-5">
                    <EmptyState
                      icon="mdi-storefront-outline"
                      :title="$t('redeem.emptyTitle')"
                      :description="$t('redeem.emptyDesc')"
                      :actionText="$t('redeem.addItem')"
                      actionTo="/redeem/add"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="p-3" v-if="filteredItems.length > itemsPerPage">
            <PaginationComponents
              :totalItems="filteredItems.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getRedeems, deleteRedeem } from "../../services/redeemService";
import PaginationComponents from '../../components/PaginationComponents.vue';
import EmptyState from '../../components/EmptyState.vue';
import Swal from 'sweetalert2';

export default {
  components: {
    PaginationComponents,
    EmptyState
  },
  data() {
    return {
      search: "",
      redeems: [],
      currentPage: 1,
      itemsPerPage: 10,
      fetchDone: false,
    };
  },
  computed: {
    filteredItems() {
      if (!this.search) return this.redeems;
      return this.redeems.filter(item => {
        return (item.name || '').toLowerCase().includes(this.search.toLowerCase());
      });
    },
    paginatedRedeem() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredItems.slice(start, end);
    }
  },
  watch: {
    search() {
      this.currentPage = 1;
    }
  },
  methods: {
    async fetchRedeems() {
      try {
        const response = await getRedeems();
        this.redeems = response.data || [];
      } catch (err) {
        console.error('Error fetching redeem items:', err);
      } finally {
        this.fetchDone = true;
      }
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
    handleDelete(id) {
      Swal.fire({
        title: this.$t('redeem.confirmDelete'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deleteRedeem(id);
            Swal.fire({
              icon: 'success',
              title: this.$t('common.success'),
              text: this.$t('redeem.deletedSuccess'),
              confirmButtonColor: '#3b82f6',
            });
            this.fetchRedeems();
          } catch (err) {
            Swal.fire({
              icon: 'error',
              title: this.$t('common.error'),
              text: this.$currentLocale === 'id' ? 'Gagal menghapus item hadiah' : 'Failed to delete reward item',
            });
          }
        }
      });
    }
  },
  mounted() {
    this.fetchRedeems();
  }
};
</script>

<style scoped>
.search-box {
  background: #18181b;
  border: 1px solid #27272a;
  border-radius: 8px;
  padding: 2px 10px;
}
.search-box:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px #3b82f6;
}
.btn-action-icon {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  line-height: 1 !important;
  transition: all 0.15s ease-in-out;
}
.btn-action-icon:hover {
  transform: translateY(-1px);
}
</style>
